import S, { DataSignal } from 's-js';
import TZDate from 'lib/TZDate';
import { MilestoneTypes as MSType } from './MilestoneTypes';
import { ScheduleDateEntity as ServerScheduleDateEntity, ScheduleDateEntityOptions, ScheduleDateEntityData } from 'server/models/ScheduleDateEntity';
import { ScheduleDateType as SDType } from './ScheduleDateType';
import { ScheduleDate, EffectiveDate } from './ScheduleDate';

export { ScheduleDateEntityOptions, ScheduleDateEntityData };

export class ScheduleDateEntity extends ServerScheduleDateEntity {
    Estimated : DataSignal<TZDate>;
    Actual : DataSignal<TZDate | null>;
    Anchor : DataSignal<TZDate>;
    Effective : DataSignal<TZDate>;

    constructor();
    constructor(data : ScheduleDateEntityOptions);
    constructor(data: ScheduleDateEntityOptions | null = null) {
        super(data!);

        // get the ScheduleDate entity stored in the parent
        const getParentDate = () =>
            (this.Type() === SDType.IPArrival   ? this.ItineraryPoint() && this.ItineraryPoint()!.Arrival :
             this.Type() === SDType.IPDeparture ? this.ItineraryPoint() && this.ItineraryPoint()!.Departure :
             this.Type() === SDType.PJOStart    ? this.JobOrder() && this.JobOrder()!.PilotJobOrder() && this.JobOrder()!.PilotJobOrder()!.Start :
             this.Type() === SDType.PJOEnd      ? this.JobOrder() && this.JobOrder()!.PilotJobOrder() && this.JobOrder()!.PilotJobOrder()!.End :
             null)
            || new ScheduleDate();

        this.Estimated = (val? : TZDate) => val === undefined ? getParentDate().Estimated() : S.sample(getParentDate).Estimated(val);
        this.Actual = (val? : TZDate | null) => val === undefined ? getParentDate().Actual() : S.sample(getParentDate).Actual(val);
        this.Anchor = (val? : TZDate) => val === undefined ? getParentDate().Anchor() : S.sample(getParentDate).Anchor(val);
        this.Effective = EffectiveDate(this);
    }

    IsMovementStartDate() {
        return (
            this.MilestoneTypeId() === MSType.FirstArrival ||
            this.MilestoneTypeId() === MSType.ShiftDeparture ||
            this.MilestoneTypeId() === MSType.FinalDeparture
        );
    }

    IsBerthArrivalDate() {
        return (
            this.MilestoneTypeId() === MSType.BerthArrival
        );
    }

    // NOTE: NextMilestoneDate and PreviousMilestoneDate mirrored in C# in
    // PortCall.Models\Schedule\ScheduleDateEntity.cs. Keep in sync.
    NextMilestoneDate() {
        const
            pc = this.PortCall(),
            sched = pc?.MovementSchedule();
        let i = sched?.indexOf(this);
        if (i === undefined || i === -1) return null;
        for (i = i + 1; i < sched!.length; i++) {
            const date = sched![i];
            // Refuse to go forwards past a null date, on the thinking that the missing/unloaded date may just
            // have been the next milestone date.
            if (date === null) return null;
            else if (date.MilestoneTypeId() !== null) return date;
        }
        return null;
    }

    PreviousMilestoneDate() {
        const
            pc = this.PortCall(),
            sched = pc?.MovementSchedule();
        let i = sched?.indexOf(this);
        if (i === undefined || i === -1) return null;
        for (i = i - 1; i >= 0; i--) {
            const date = sched![i];
            // Refuse to go backwards past a null date, on the thinking that the missing/unloaded date may just
            // have been the previous milestone date.
            if (date === null) return null;
            else if (date.MilestoneTypeId() !== null) return date;
        }
        return null;
    }

    // NOTE: Coordinations... methods mirrored in C# in PortCall.Models\Schedule\ScheduleDateEntity.cs.
    // Keep in sync.
    CoordinationsMainLocation() {
        return this.ItineraryPoint()?.Location() ?? null;
    }

    CoordinationsRelatedLocation() {
        return this.CoordinationsRelatedDate()?.ItineraryPoint()?.Location() ?? null;
    }

    CoordinationsRelatedDate() {
        if (this.MilestoneTypeId() === null) return null;
        return (
            // For berth arrival, look to previous milestone date.
            this.MilestoneTypeId() === MSType.BerthArrival ? this.PreviousMilestoneDate() :
            // For final departure, look to next milestone date for future proofing, then to port departure b/c
            // we don't actually have a milestone type for port departure currently.
            this.MilestoneTypeId() === MSType.FinalDeparture ?
                this.NextMilestoneDate() ?? this.PortCall()?.Itinerary().find(pt => pt.IsPortDeparture())?.ArrivalEntity() ?? null :
            // All others -- Look to next milestone date.
            this.NextMilestoneDate()
        );
    }
}



// WEBPACK FOOTER //
// ./data/models/ScheduleDateEntity.ts